// Each machine's moves budget as the page last heard it (#6772, review round 2): the answers a
// move, a preview or a refusal brings, laid over the machine row's `moves` (`./move-meter.ts`
// `effectiveMoves`), and the rules that keep an older answer from undoing a newer one.
//
// A PER-MACHINE MUTATION ORDER. A move, an Undo and each Put-back part is a mutation: when its
// POST is SENT, both machines' latest-mutation stamp moves forward (`orderStamp`) and their
// in-flight count goes up; when it answers, the count goes down.
// - A preview is applied only if no mutation was in flight on its machine when it started or
//   when it answered, and none was sent after it started: one that overlapped a mutation may
//   have read the balance before it, and only a read after it is trusted.
// - A mutation's own answer (its 201's budgets, or a refusal body's `left`) is applied only if
//   no later mutation was sent to that machine since; the later one's answer will be.
// - While a mutation is in flight on a machine, its overlay outranks the row: a fleet read that
//   overlaps the POST may not show it. After a 201 the page re-reads the fleet, and that read,
//   started after the answer, replaces the overlay.
//
// NO REVERT AT FREE_AT. The overlay is kept when its `free_at` passes: the row under it may have
// been read before the moves the overlay counted, and reverting to it would un-charge them. The
// machine's budget is read again instead (`reread`, once per expiry), and the overlay stays until
// that read lands.

import { createStore } from 'solid-js/store';
import type { MovesOverlay } from './move-meter';
import { orderStamp } from './read-order';

/** What a preview carries from when it started to when it answers. */
export type PreviewTicket = {
  /** When it started, in the page's order: a fleet read that started later outranks its answer. */
  readonly order: number;
  /** No mutation was in flight on its machine when it started. */
  readonly clean: boolean;
};

/** One machine's budget in an answer. */
export type BudgetAnswer = {
  readonly boxId: string;
  readonly left: number;
  readonly free_at: string | null;
  /** The machine row the answer is laid over, held to tell a newer read by identity. */
  readonly row: unknown;
};

export function createMoveBudgets(o: {
  readonly gone: () => boolean;
  readonly tickUntil: (untilMs: number | null) => void;
  /** Read the fleet again: a passed `free_at` is settled by a fresh read, never by reverting. */
  readonly reread: () => void;
}) {
  // A store keyed by machine, so an answer for one machine re-runs only that machine's readers.
  const [overlays, setOverlays] = createStore<Record<string, MovesOverlay | undefined>>({});
  const [inFlight, setInFlight] = createStore<Record<string, number | undefined>>({});
  /** The row each overlay was laid over (unwrapped: a store would hand back a proxy) and its order. */
  const laid = new Map<string, { readonly row: unknown; readonly order: number }>();
  /** Each machine's newest mutation, by when its POST was sent. */
  const lastSent = new Map<string, number>();
  /** The `free_at` each machine was last re-read for: one entry per machine, never per expiry. */
  const reread = new Map<string, string>();

  /** Apply `a`, from the mutation or preview at `order`, as fresh as `fresh` in the page's order. */
  const apply = (a: BudgetAnswer, order: number, fresh: number): void => {
    const prior = laid.get(a.boxId);
    if (prior !== undefined && prior.order > order) return;
    laid.set(a.boxId, { row: a.row, order });
    setOverlays(a.boxId, { left: a.left, free_at: a.free_at, order: fresh });
    o.tickUntil(a.free_at === null ? null : Date.parse(a.free_at));
  };
  const busy = (boxId: string): boolean => (inFlight[boxId] ?? 0) > 0;
  return {
    /** The answer laid over a machine and its row, or undefined. */
    laidOf(boxId: string): { readonly overlay: MovesOverlay; readonly row: unknown } | undefined {
      const overlay = overlays[boxId];
      return overlay === undefined ? undefined : { overlay, row: laid.get(boxId)?.row };
    },
    /** A mutation is in flight on the machine; tracked. */
    busy,
    /**
     * A mutation's POST is being sent to these machines. Returns its ticket: the order it was
     * sent at, and `settle`, which applies its answer's budgets where no later POST was sent
     * and releases its in-flight count. A ticket settles ONCE (#6772 review round 11): a second
     * call is a no-op, so no path can release another POST's count.
     */
    mutationSent(boxIds: readonly string[]): MutationTicket {
      const order = orderStamp();
      for (const id of boxIds) {
        lastSent.set(id, order);
        setInFlight(id, (n) => (n ?? 0) + 1);
      }
      let settled = false;
      const settle = (answers: readonly BudgetAnswer[]): void => {
        if (settled) return;
        settled = true;
        for (const id of boxIds) setInFlight(id, (n) => Math.max(0, (n ?? 0) - 1));
        if (o.gone()) return;
        // Fresh as of NOW in the page's order: a fleet read that started before this answer
        // settled may not show it, one that started after does.
        const fresh = orderStamp();
        for (const a of answers) if (lastSent.get(a.boxId) === order) apply(a, order, fresh);
      };
      return { order, settle };
    },
    /** A preview is starting on this machine. */
    previewStarted(boxId: string): PreviewTicket {
      return { order: orderStamp(), clean: !busy(boxId) };
    },
    /** A preview answered: applied only if it overlapped no mutation on its machine. */
    previewAnswered(t: PreviewTicket, a: BudgetAnswer): void {
      if (o.gone() || !t.clean || busy(a.boxId)) return;
      if ((lastSent.get(a.boxId) ?? 0) > t.order) return;
      apply(a, t.order, t.order);
    },
    /** Re-read the fleet once for every overlay whose `free_at` has passed at `now`. */
    expire(now: number): void {
      for (const boxId of laid.keys()) {
        const free = overlays[boxId]?.free_at ?? null;
        if (free === null || Date.parse(free) > now) continue;
        if (reread.get(boxId) === free) continue;
        reread.set(boxId, free);
        o.reread();
      }
    },
  };
}

export type MoveBudgets = ReturnType<typeof createMoveBudgets>;

/** One mutation's POST, in flight until `settle` (which runs once) answers it. */
export type MutationTicket = {
  readonly order: number;
  readonly settle: (answers: readonly BudgetAnswer[]) => void;
};
